import React, { useEffect, useState } from "react";
import { getDigitalTwin } from "../../../api/digitalTwinShipmentService";

export default function ClientDigitalTwinPage() {
    const navigate = useNavigate();
    const [shipmentId, setShipmentId] = useState("");
    const [twin, setTwin] = useState(null);
    const [error, setError] = useState("");
    const [trackingNumber, setTrackingNumber] = useState("");
    const [liveStatus, setLiveStatus] = useState("Disconnected");
    const [livePosition, setLivePosition] = useState(null);
    const loadTwin = async () => {
        try {
            setError("");
            setTwin(null);

            const data = await getDigitalTwin(shipmentId);
            setTwin(data);
            const tracking =
                data?.shipment?.trackingNumber ||
                data?.trackingNumber ||
                "";

            setTrackingNumber(tracking);
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to load digital twin."
            );
        }
    };
    useEffect(() => {
        if (!trackingNumber) return;

        const apiUrl = import.meta.env.VITE_API_URL || "https://api.idlogichain.com";

        const wsUrl = apiUrl
            .replace("https://", "wss://")
            .replace("http://", "ws://");

        const socket = new WebSocket(
            `${wsUrl}/ws/iot?tracking=${encodeURIComponent(trackingNumber)}`
        );

        setLiveStatus("Connecting");

        socket.onopen = () => {
            setLiveStatus("Connected");
        };

        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.type === "iot_update") {
                    setLivePosition({
                        latitude: data.latitude,
                        longitude: data.longitude,
                        updatedAt: data.updatedAt,
                        autoStatus: data.autoStatus,
                        eta: data.eta,
                    });

                    if (shipmentId) {
                        loadTwin();
                    }
                }
            } catch {
                // ignore invalid websocket message
            }
        };

        socket.onerror = () => {
            setLiveStatus("Error");
        };

        socket.onclose = () => {
            setLiveStatus("Disconnected");
        };

        return () => {
            socket.close();
        };
    }, [trackingNumber]);
    return (
        <div className="max-w-5xl mx-auto p-6 bg-white rounded shadow">
            <h2 className="text-2xl font-bold mb-4">
                My Shipment Digital Twin
            </h2>

            {error && <p className="text-red-600 mb-3">{error}</p>}

            <div className="flex gap-3 mb-6">
                <input
                    type="number"
                    value={shipmentId}
                    onChange={(e) => setShipmentId(e.target.value)}
                    placeholder="Shipment ID"
                    className="border p-2 rounded w-48"
                />

                <button
                    onClick={loadTwin}
                    className="bg-blue-700 text-white px-4 py-2 rounded"
                >
                    View Digital Twin
                </button>
                <button
                    onClick={() => navigate("/client/digital-twin")}
                    className="bg-blue-700 text-white px-4 py-2 rounded"
                >
                    Digital Twin
                </button>
            </div>

            {twin && (
                <div className="space-y-6">
                    <div className="border rounded p-4 bg-gray-50">
                        <h3 className="text-xl font-bold mb-3">Shipment Twin Summary</h3>

                        <p><b>Shipment ID:</b> {twin.shipmentId}</p>
                        <p><b>Status:</b> {twin.currentStatus || "�"}</p>
                        <p><b>Trust Score:</b> {twin.trustScore}</p>
                        <p><b>Risk Level:</b> {twin.riskLevel}</p>
                        <p><b>Anomaly Detected:</b> {twin.hasAnomaly ? "Yes" : "No"}</p>
                        <p><b>Requires Inspection:</b> {twin.requiresInspection ? "Yes" : "No"}</p>
                        <p>
                            <b>Last Updated:</b>{" "}
                            {twin.lastUpdatedAt
                                ? new Date(twin.lastUpdatedAt).toLocaleString()
                                : "�"}
                        </p>
                    </div>
                    {trackingNumber && (
                        <div className="border rounded p-4 mb-6 bg-blue-50">
                            <h3 className="text-lg font-bold mb-3">
                                Live Shipment Tracking
                            </h3>

                            <p><b>Tracking Number:</b> {trackingNumber}</p>
                            <p><b>WebSocket:</b> {liveStatus}</p>

                            {livePosition ? (
                                <>
                                    <p><b>Latitude:</b> {livePosition.latitude}</p>
                                    <p><b>Longitude:</b> {livePosition.longitude}</p>
                                    <p><b>Status:</b> {livePosition.autoStatus || "�"}</p>
                                    <p>
                                        <b>Updated:</b>{" "}
                                        {livePosition.updatedAt
                                            ? new Date(livePosition.updatedAt).toLocaleString()
                                            : "�"}
                                    </p>
                                </>
                            ) : (
                                <p className="text-gray-600">
                                    Waiting for live IoT updates...
                                </p>
                            )}
                        </div>
                    )}
                    <div className="border rounded p-4">
                        <h3 className="text-lg font-bold mb-3">Latest Telemetry</h3>

                        {twin.telemetryRecords?.length > 0 ? (
                            <table className="w-full border text-sm">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border p-2">Latitude</th>
                                        <th className="border p-2">Longitude</th>
                                        <th className="border p-2">IoT Status</th>
                                        <th className="border p-2">Recorded At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {twin.telemetryRecords.map((t) => (
                                        <tr key={t.id}>
                                            <td className="border p-2">{t.latitude ?? "�"}</td>
                                            <td className="border p-2">{t.longitude ?? "�"}</td>
                                            <td className="border p-2">{t.iotStatus || "�"}</td>
                                            <td className="border p-2">
                                                {t.recordedAt
                                                    ? new Date(t.recordedAt).toLocaleString()
                                                    : "�"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="text-gray-500">No telemetry records available.</p>
                        )}
                    </div>

                    <div className="border rounded p-4">
                        <h3 className="text-lg font-bold mb-3">Security Status</h3>

                        {twin.securityStatuses?.length > 0 ? (
                            <table className="w-full border text-sm">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border p-2">GeoFence</th>
                                        <th className="border p-2">Blockchain</th>
                                        <th className="border p-2">Route</th>
                                        <th className="border p-2">ETA</th>
                                        <th className="border p-2">Created At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {twin.securityStatuses.map((s) => (
                                        <tr key={s.id}>
                                            <td className="border p-2">{s.geoFenceStatus || "�"}</td>
                                            <td className="border p-2">{s.blockchainStatus || "�"}</td>
                                            <td className="border p-2">{s.routeStatus || "�"}</td>
                                            <td className="border p-2">{s.etaStatus || "�"}</td>
                                            <td className="border p-2">
                                                {s.createdAt
                                                    ? new Date(s.createdAt).toLocaleString()
                                                    : "�"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="text-gray-500">No security records available.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}